import { useActionContext } from '../context/ActionContext';
import { useNamespaces } from '../context/NamespacesContext';
import { useLoopItem } from '../context/LoopItemContext';
import Button from '../primitives/Button/Button';
import OptionButton from '../primitives/OptionButton/OptionButton';
import Calendar from '../primitives/Calendar/Calendar';
import Checkbox from '../primitives/Checkbox/Checkbox';
import If from '../primitives/If/If';
import Input from '../primitives/Input/Input';
import Loop from '../primitives/Loop/Loop';
import Row from '../primitives/Row/Row';
import AutoColumns from '../primitives/AutoColumns/AutoColumns';
import Select from '../primitives/Select/Select';
import SlotList from '../primitives/SlotList/SlotList';
import TimezonePicker from '../primitives/TimezonePicker/TimezonePicker';
import Stack from '../primitives/Stack/Stack';
import Textarea from '../primitives/Textarea/Textarea';
import Icon from '../primitives/Icon/Icon';
import SvgIcon from '../primitives/SvgIcon/SvgIcon';
import TextBlock from '../primitives/TextBlock/TextBlock';
import Text from '../primitives/Text/Text';
import { resolveBindable, resolveBindableBoolean } from '../binding/operators/resolveBindable';
import { applyFormat } from '../binding/operators/applyFormat';
import { resolveFormatSpec } from '../binding/operators/resolveFormatSpec';
import { resolveBindKey } from '../binding/operators/resolveBindKey';
import { jsx as _jsx } from "react/jsx-runtime";
const toDisplayString = value => value === undefined || value === null ? '' : String(value);
const hasOwnFormat = label => typeof label === 'object' && label !== null && '$format' in label;
function isResolvedArray(value) {
  return Array.isArray(value);
}
export const RenderElement = ({
  element,
  mutableContext,
  onMutableContextChange
}) => {
  const namespaces = useNamespaces();
  const loopItem = useLoopItem();
  const {
    onButtonClick,
    getActionState,
    isCardDisabled,
    allRequiredBindingsFilled,
    isReadOnly
  } = useActionContext();
  const resolveBinding = value => resolveBindable(value, namespaces, loopItem);
  const resolveBooleanBinding = value => resolveBindableBoolean(value, namespaces, loopItem);
  const isElementDisabled = schemaDisabled => !!resolveBooleanBinding(schemaDisabled) || isCardDisabled || isReadOnly;
  switch (element.type) {
    case 'TextBlock':
      {
        const text = resolveBinding(element.text);
        if (!text) return null;
        return /*#__PURE__*/_jsx(TextBlock, {
          text: text,
          size: resolveBinding(element.size),
          weight: resolveBinding(element.weight),
          color: resolveBinding(element.color),
          align: resolveBinding(element.align)
        });
      }
    case 'Text':
      {
        const text = resolveBinding(element.text);
        if (!text) return null;
        return /*#__PURE__*/_jsx(Text, {
          text: text,
          size: resolveBinding(element.size),
          weight: resolveBinding(element.weight),
          color: resolveBinding(element.color)
        });
      }
    case 'Button':
      {
        const label = resolveBinding(element.label);
        if (!label) return null;
        const resolvedAdditionalParams = element.additionalParams ? Object.entries(element.additionalParams).reduce((acc, [k, v]) => {
          const resolved = resolveBinding(v);
          if (resolved !== undefined) acc[k] = resolved;
          return acc;
        }, {}) : undefined;
        const resolvedVariant = resolveBinding(element.variant);
        const isNavigationButton = resolvedVariant === 'link';
        const contentNodes = element.content ? element.content.map((item, i) => /*#__PURE__*/_jsx(RenderElement, {
          element: item,
          mutableContext: mutableContext,
          onMutableContextChange: onMutableContextChange
        }, `${item.type}-${i}`)) : undefined;
        return /*#__PURE__*/_jsx(Button, {
          label: label,
          variant: resolvedVariant,
          disabled: isElementDisabled(element.disabled),
          fullWidth: resolveBooleanBinding(element.fullWidth),
          onClick: () => onButtonClick(element.callAction, resolvedAdditionalParams),
          actionState: getActionState(element.callAction),
          allRequiredBindingsFilled: isNavigationButton ? true : allRequiredBindingsFilled,
          children: contentNodes
        });
      }
    case 'OptionButton':
      {
        var _mutableContext$name;
        const name = resolveBindKey(element.value, namespaces, loopItem);
        if (!name) return null;
        const rawOptionValue = resolveBinding(element.optionValue);
        if (rawOptionValue === undefined) return null;
        const optionValue = String(rawOptionValue);
        const label = resolveBinding(element.label);
        if (!label) return null;
        const isSelected = ((_mutableContext$name = mutableContext[name]) !== null && _mutableContext$name !== void 0 ? _mutableContext$name : '') === optionValue;
        return /*#__PURE__*/_jsx(OptionButton, {
          name: name,
          optionValue: optionValue,
          label: label,
          isSelected: isSelected,
          isDisabled: isElementDisabled(element.disabled),
          onChange: onMutableContextChange
        });
      }
    case 'Input':
      {
        var _mutableContext$name2;
        const name = resolveBindKey(element.value, namespaces, loopItem);
        if (!name) return null;
        return /*#__PURE__*/_jsx(Input, {
          name: name,
          value: (_mutableContext$name2 = mutableContext[name]) !== null && _mutableContext$name2 !== void 0 ? _mutableContext$name2 : '',
          label: resolveBinding(element.label),
          labelLayout: resolveBinding(element.labelLayout),
          inputType: resolveBinding(element.inputType),
          placeholder: resolveBinding(element.placeholder),
          required: resolveBooleanBinding(element.required),
          onChange: onMutableContextChange,
          isDisabled: isElementDisabled(element.disabled)
        });
      }
    case 'Textarea':
      {
        var _mutableContext$name3;
        const name = resolveBindKey(element.value, namespaces, loopItem);
        if (!name) return null;
        return /*#__PURE__*/_jsx(Textarea, {
          name: name,
          value: (_mutableContext$name3 = mutableContext[name]) !== null && _mutableContext$name3 !== void 0 ? _mutableContext$name3 : '',
          label: resolveBinding(element.label),
          labelLayout: resolveBinding(element.labelLayout),
          placeholder: resolveBinding(element.placeholder),
          required: resolveBooleanBinding(element.required),
          onChange: onMutableContextChange,
          isDisabled: isElementDisabled(element.disabled)
        });
      }
    case 'Checkbox':
      {
        const name = resolveBindKey(element.value, namespaces, loopItem);
        if (!name) return null;
        const label = resolveBinding(element.label);
        if (!label) return null;
        return /*#__PURE__*/_jsx(Checkbox, {
          name: name,
          checked: mutableContext[name] === 'true',
          label: label,
          required: resolveBooleanBinding(element.required),
          onChange: onMutableContextChange,
          isDisabled: isElementDisabled(element.disabled)
        });
      }
    case 'Select':
      {
        var _mutableContext$name4;
        const name = resolveBindKey(element.value, namespaces, loopItem);
        if (!name) return null;
        const optionSpecs = resolveBinding(element.options);
        if (!isResolvedArray(optionSpecs)) return null;
        const optionFormat = element.optionFormat ? resolveFormatSpec(element.optionFormat, namespaces, loopItem) : undefined;
        const options = optionSpecs.map(option => {
          const resolved = resolveBinding(option.label);
          const label = optionFormat && !hasOwnFormat(option.label) ? applyFormat(resolved, optionFormat, namespaces.system.locale) : toDisplayString(resolved);
          return {
            value: option.value,
            label
          };
        });
        return /*#__PURE__*/_jsx(Select, {
          name: name,
          value: (_mutableContext$name4 = mutableContext[name]) !== null && _mutableContext$name4 !== void 0 ? _mutableContext$name4 : '',
          options: options,
          label: resolveBinding(element.label),
          labelLayout: resolveBinding(element.labelLayout),
          width: resolveBinding(element.width),
          placeholder: resolveBinding(element.placeholder),
          required: resolveBooleanBinding(element.required),
          onChange: onMutableContextChange,
          isDisabled: isElementDisabled(element.disabled)
        });
      }
    case 'SlotList':
      {
        var _resolveBinding, _mutableContext$name5;
        const name = resolveBindKey(element.value, namespaces, loopItem);
        if (!name) return null;
        const slotSpecs = resolveBinding(element.slots);
        if (!isResolvedArray(slotSpecs)) return null;
        const slotFormat = element.slotFormat ? resolveFormatSpec(element.slotFormat, namespaces, loopItem) : undefined;
        const slots = slotSpecs.map(slot => {
          const resolved = resolveBinding(slot.label);
          const label = slotFormat && !hasOwnFormat(slot.label) ? applyFormat(resolved, slotFormat, namespaces.system.locale) : toDisplayString(resolved);
          return {
            value: slot.value,
            group: slot.group,
            label
          };
        });
        const group = element.group !== undefined ? (_resolveBinding = resolveBinding(element.group)) !== null && _resolveBinding !== void 0 ? _resolveBinding : '' : undefined;
        return /*#__PURE__*/_jsx(SlotList, {
          name: name,
          selectedValue: (_mutableContext$name5 = mutableContext[name]) !== null && _mutableContext$name5 !== void 0 ? _mutableContext$name5 : '',
          slots: slots,
          group: group,
          maxVisibleSlots: resolveBinding(element.maxVisibleSlots),
          onChange: onMutableContextChange,
          isDisabled: isElementDisabled(element.disabled)
        });
      }
    case 'TimezonePicker':
      {
        var _mutableContext$name6;
        const name = resolveBindKey(element.value, namespaces, loopItem);
        if (!name) return null;
        return /*#__PURE__*/_jsx(TimezonePicker, {
          name: name,
          value: (_mutableContext$name6 = mutableContext[name]) !== null && _mutableContext$name6 !== void 0 ? _mutableContext$name6 : '',
          label: resolveBinding(element.label),
          labelLayout: resolveBinding(element.labelLayout),
          width: resolveBinding(element.width),
          required: resolveBooleanBinding(element.required),
          isDisabled: isElementDisabled(element.disabled),
          onChange: onMutableContextChange
        });
      }
    case 'Calendar':
      {
        const name = resolveBindKey(element.value, namespaces, loopItem);
        if (!name) return null;
        const resolvedAvailableDates = resolveBinding(element.availableDates);
        const availableDates = isResolvedArray(resolvedAvailableDates) ? resolvedAvailableDates : [];
        return /*#__PURE__*/_jsx(Calendar, {
          name: name,
          selectedDate: mutableContext[name],
          availableDates: availableDates,
          timezone: resolveBinding(element.timezone),
          isDisabled: isElementDisabled(element.disabled),
          onChange: onMutableContextChange
        });
      }
    case 'Stack':
      return /*#__PURE__*/_jsx(Stack, {
        gap: element.gap,
        padding: element.padding,
        align: element.align,
        justify: element.justify,
        grow: element.grow,
        overflow: element.overflow,
        maxWidth: element.maxWidth,
        maxHeight: element.maxHeight,
        border: element.border,
        children: element.children.map((child, i) => /*#__PURE__*/_jsx(RenderElement, {
          element: child,
          mutableContext: mutableContext,
          onMutableContextChange: onMutableContextChange
        }, i))
      });
    case 'Row':
      return /*#__PURE__*/_jsx(Row, {
        gap: element.gap,
        justify: element.justify,
        align: element.align,
        wrap: element.wrap,
        breakAt: element.breakAt,
        children: element.children.map((child, i) => /*#__PURE__*/_jsx(RenderElement, {
          element: child,
          mutableContext: mutableContext,
          onMutableContextChange: onMutableContextChange
        }, i))
      });
    case 'AutoColumns':
      return /*#__PURE__*/_jsx(AutoColumns, {
        minColumnWidth: element.minColumnWidth,
        gap: element.gap,
        divider: element.divider,
        anchorFirst: element.anchorFirst,
        children: element.children.map((child, i) => /*#__PURE__*/_jsx(RenderElement, {
          element: child,
          mutableContext: mutableContext,
          onMutableContextChange: onMutableContextChange
        }, i))
      });
    case 'If':
      return /*#__PURE__*/_jsx(If, {
        element: element,
        mutableContext: mutableContext,
        onMutableContextChange: onMutableContextChange
      });
    case 'Loop':
      return /*#__PURE__*/_jsx(Loop, {
        element: element,
        mutableContext: mutableContext,
        onMutableContextChange: onMutableContextChange
      });
    case 'Icon':
      {
        const name = resolveBinding(element.name);
        if (!name) return null;
        return /*#__PURE__*/_jsx(Icon, {
          name: name,
          style: resolveBinding(element.style),
          size: resolveBinding(element.size),
          color: resolveBinding(element.color),
          ariaLabel: resolveBinding(element.ariaLabel)
        });
      }
    case 'SvgIcon':
      {
        const svg = resolveBinding(element.svg);
        if (!svg) return null;
        return /*#__PURE__*/_jsx(SvgIcon, {
          svg: svg,
          ariaLabel: resolveBinding(element.ariaLabel)
        });
      }
    default:
      return null;
  }
};